/* eslint-disable no-case-declarations */
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { FormControl } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FieldNames from '../../../defaults/FieldNames.json';
import { formatDateForDisplay } from '../../../utils/DateHelper';
import { getFieldStyle } from '../../../utils/ValidationHelper';
import type Graphic from '@arcgis/core/Graphic';
import {
    addTreatmentFieldToValidationErrorFields,
    removeTreatmentFieldFromValidationErrorFields,
} from '../../../redux/slices/treatmentValidationSlice';
import {
    addActivityFieldToValidationErrorFields,
    removeActivityFieldFromValidationErrorFields,
} from '../../../redux/slices/activityValidationSlice';
import {
    addActualTreatmentFieldToValidationErrorFields,
    removeActualTreatmentFieldFromValidationErrorFields,
} from '../../../redux/slices/actualsTreatmentValidationSlice';
import {
    addActualActivityFieldToValidationErrorFields,
    removeActualActivityFieldFromValidationErrorFields,
} from '../../../redux/slices/actualsActivityValidationSlice';

const useStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid red',
        },
        '&&:after': {
            borderBottom: '2px solid red',
        },
    },
});

const useNotSavedStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid gold',
        },
        '&&:after': {
            borderBottom: '2px solid gold',
        },
    },
});

const theme = createTheme({});

export default function DetailsDatePicker({
    detail,
    field,
    validators,
    onFieldChange,
    isLocallyApproved,
    setIsLocallyApproved,
    isRegionalApproved,
    setIsRegionalApproved,
    isAgencyApproved,
    setIsAgencyApproved,
    isDepartmentApproved,
    setIsDepartmentApproved,
    isRequired,
    getAllMissingFieldsCount,
    allFields,
    getAllApprovalFieldsCount,
    isPast,
    userInfo,
    userRoleInfo,
    isCancelled,
    onFiscalFieldChange,
    isActuals,
    departmentValue,
    agencyValue,
    regionValue,
    unitValue,
    fundingTribeValue,
    isTreatment,
}) {
    const dispatch = useDispatch();
    const treatmentValidationUnsavedValues = useSelector((state: any) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state: any) => {
        return state.activityValidation;
    });
    const actualsTreatmentValidationUnsavedValues = useSelector((state: any) => {
        return state.actualsTreatmentValidation;
    });
    const actualsActivityValidationUnsavedValues = useSelector((state: any) => {
        return state.actualsActivityValidation;
    });

    const memoizedValidators = useMemo(() => validators, [validators]);

    const classes = useStyles();
    const notSavedClasses = useNotSavedStyles();

    const [datePickerValue, setDatePickerValue] = useState(null);
    const [currentValue, setCurrentValue] = useState(null);
    const [currentError, setCurrentError] = useState('');
    const [toShowError, setToShowError] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);

    useEffect(() => {
        if (detail && detail.attributes && detail.attributes[field]) {
            setDatePickerValue(detail.attributes[field]);
        } else {
            setDatePickerValue(null);
        }

        if (isActuals && field === allFields.completionDate) {
            setIsReadOnly(true);
        }
    }, [
        detail,
        field,
        isLocallyApproved,
        isRegionalApproved,
        isDepartmentApproved,
        isAgencyApproved,
        allFields,
        isActuals,
    ]);

    useEffect(() => {
        validateValue(currentValue, detail, isActuals);
    }, [currentValue, detail, isActuals, memoizedValidators]);

    const callValidators = (value: number | Date | null, detail: Graphic, isActuals: boolean) => {
        return memoizedValidators
            ? memoizedValidators
                  .flatMap((val: Function) => {
                      const result = val(value, detail, isActuals);
                      return Array.isArray(result) ? result : result ? [result] : [];
                  })
                  .join(', ')
            : '';
    };

    const handleChange = (value) => {
        if (value === null) {
            setCurrentValue(value);
            setCurrentError('');
            setToShowError(false);
            if (field === allFields.completionDate || field === allFields.initiationDate) {
                onFiscalFieldChange(field, value);
            } else {
                onFieldChange(field, value);
            }
            if (field === allFields.localApprovalDate) {
                setIsLocallyApproved(false);
            } else if (field === allFields.regionalApprovalDate) {
                setIsRegionalApproved(false);
            } else if (field === allFields.agencyApprovalDate) {
                setIsAgencyApproved(false);
            } else if (field === allFields.departmentApprovalDate) {
                setIsDepartmentApproved(false);
            }
        } else {
            if (!isNaN(Date.parse(value))) {
                setCurrentValue(value);
                setCurrentError('');
                setToShowError(false);

                if (
                    field === allFields.completionDate ||
                    field === allFields.initiationDate ||
                    field === allFields.cancelledDate ||
                    field === allFields.unApprovalDate
                ) {
                    onFiscalFieldChange(field, value);
                } else {
                    onFieldChange(field, value);
                }
            } else {
                setCurrentError('Invalid Date Format');
                setToShowError(true);
                if (field === allFields.localApprovalDate) {
                    setIsLocallyApproved(false);
                } else if (field === allFields.regionalApprovalDate) {
                    setIsRegionalApproved(false);
                } else if (field === allFields.agencyApprovalDate) {
                    setIsAgencyApproved(false);
                } else if (field === allFields.departmentApprovalDate) {
                    setIsDepartmentApproved(false);
                }
            }
        }
    };

    const validateValue = (value: number | Date | null, detail: Graphic, isActuals: boolean) => {
        const dateErrString = validators ? callValidators(value, detail, isActuals) : '';
        handleFieldErrorString(dateErrString);
    };

    const disableDateField = (field) => {
        if (field === allFields.createdOnDate || field === allFields.lastModifiedDate) {
            return true;
        }
        if (field === allFields.fireDiscoveryDate || field === allFields.containmentDate) {
            return true;
        }
        if (
            field !== allFields.localApprovalDate &&
            field !== allFields.regionalApprovalDate &&
            field !== allFields.agencyApprovalDate &&
            field !== allFields.departmentApprovalDate
        ) {
            if (field === allFields.completionDate) {
                if (isCancelled) {
                    return true;
                } else {
                    if (detail && detail.attributes && isActuals) {
                        const missingFieldsCount = getAllMissingFieldsCount(
                            detail,
                            isTreatment,
                            userInfo,
                            isActuals
                        );
                        if (
                            (missingFieldsCount === 1 &&
                                !detail.attributes[allFields.completionDate]) ||
                            detail.attributes[allFields.completionDate]
                        ) {
                            return false;
                        } else {
                            return true;
                        }
                    } else {
                        return false;
                    }
                }
            } else if (field === allFields.cancelledDate) {
                if (
                    detail &&
                    detail.attributes &&
                    isActuals &&
                    detail.attributes[allFields.completionDate]
                ) {
                    return true;
                } else {
                    return false;
                }
            } else if (field === allFields.unApprovalDate) {
                if (
                    detail &&
                    detail.attributes &&
                    isActuals &&
                    detail.attributes[allFields.completionDate]
                ) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return false;
            }
        } else {
            //All user roles are set to the same approval level
            if (userRoleInfo && userRoleInfo.length > 0 && detail && detail.attributes) {
                const userRole = userRoleInfo[0][FieldNames.userRoles.appRole];
                //only toggle Approval Date fields

                const missingFieldsCount = getAllMissingFieldsCount(
                    detail,
                    isTreatment,
                    userInfo,
                    isActuals
                );
                if (
                    missingFieldsCount > 0 &&
                    !(
                        detail.attributes[allFields.localApprovalDate] ||
                        detail.attributes[allFields.regionalApprovalDate] ||
                        detail.attributes[allFields.agencyApprovalDate] ||
                        detail.attributes[allFields.departmentApprovalDate]
                    )
                ) {
                    return true;
                } else {
                    if (detail && detail.attributes) {
                        if (
                            field === allFields.localApprovalDate &&
                            (userRole === FieldNames.roles.local ||
                                userRole === FieldNames.roles.region ||
                                userRole === FieldNames.roles.agency ||
                                userRole === FieldNames.roles.department)
                        ) {
                            return false;
                        } else if (
                            field === allFields.regionalApprovalDate &&
                            (userRole === FieldNames.roles.region ||
                                userRole === FieldNames.roles.agency ||
                                userRole === FieldNames.roles.department)
                        ) {
                            return false;
                        } else if (
                            field === allFields.agencyApprovalDate &&
                            (userRole === FieldNames.roles.agency ||
                                userRole === FieldNames.roles.department)
                        ) {
                            return false;
                        } else if (
                            field === allFields.departmentApprovalDate &&
                            userRole === FieldNames.roles.department &&
                            (detail.attributes[allFields.status] !==
                                FieldNames.statuses.completed ||
                                !detail.attributes[allFields.departmentApprovalDate])
                        ) {
                            //&& (!detail.attributes[allFields.departmentApprovalDate] || !detail.attributes[allFields.completionDate])){
                            return false;
                        } else {
                            return true;
                        }
                    } else {
                        return false;
                    }
                }
            } else if (
                field === allFields.departmentApprovalDate &&
                detail &&
                detail.attributes &&
                (detail.attributes[allFields.departmentApprovalDate] ||
                    !detail.attributes[allFields.completionDate])
            ) {
                return false;
            } else {
                return true;
            }
        }
    };

    const handleFieldErrorString = (dateErrString: string) => {
        setCurrentError(dateErrString);
        if (!dateErrString) {
            setToShowError(false);
            if (field === allFields.localApprovalDate) {
                setIsLocallyApproved(true);
                clearFieldError(field);
            } else if (field === allFields.regionalApprovalDate) {
                setIsRegionalApproved(true);
                clearFieldError(field);
            } else if (field === allFields.agencyApprovalDate) {
                setIsAgencyApproved(true);
                clearFieldError(field);
            } else if (field === allFields.departmentApprovalDate) {
                setIsDepartmentApproved(true);
                clearFieldError(field);
            }
        } else {
            setToShowError(true);
            if (field === allFields.localApprovalDate) {
                setIsLocallyApproved(false);
                dispatchFieldError(field);
            } else if (field === allFields.regionalApprovalDate) {
                setIsRegionalApproved(false);
                dispatchFieldError(field);
            } else if (field === allFields.agencyApprovalDate) {
                setIsAgencyApproved(false);
                dispatchFieldError(field);
            } else if (field === allFields.departmentApprovalDate) {
                setIsDepartmentApproved(false);
                dispatchFieldError(field);
            }
        }
    };

    const clearFieldError = (field: string) => {
        let dispatchRemoveFieldError;
        if (isTreatment) {
            if (isActuals) {
                dispatchRemoveFieldError = removeActualTreatmentFieldFromValidationErrorFields;
            } else {
                dispatchRemoveFieldError = removeTreatmentFieldFromValidationErrorFields;
            }
        } else {
            if (isActuals) {
                dispatchRemoveFieldError = removeActualActivityFieldFromValidationErrorFields;
            } else {
                dispatchRemoveFieldError = removeActivityFieldFromValidationErrorFields;
            }
        }

        dispatch(dispatchRemoveFieldError(field));
    };

    const dispatchFieldError = (field: string) => {
        let dispatchAddErrorField;
        if (isTreatment) {
            if (isActuals) {
                dispatchAddErrorField = addActualTreatmentFieldToValidationErrorFields;
            } else {
                dispatchAddErrorField = addTreatmentFieldToValidationErrorFields;
            }
        } else {
            if (isActuals) {
                dispatchAddErrorField = addActualActivityFieldToValidationErrorFields;
            } else {
                dispatchAddErrorField = addActivityFieldToValidationErrorFields;
            }
        }

        dispatch(dispatchAddErrorField(field));
    };

    return (
        <FormControl variant='standard' fullWidth>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                        format='MM/dd/yyyy'
                        fullWidth
                        clearText='Clear'
                        disabled={
                            field === FieldNames.featureFields.unApprovalDate
                                ? true
                                : (getAllApprovalFieldsCount &&
                                        getAllApprovalFieldsCount(detail, isRequired)) ||
                                    disableDateField(field)
                                  ? true
                                  : false
                        }
                        id={`datePicker-${field}`}
                        value={datePickerValue}
                        onChange={(value) => handleChange(value)}
                        inputProps={{ readOnly: isReadOnly }} // Sets it so that the field can't be typed into for Completion only
                        disableOpenPicker={field === FieldNames.featureFields.unApprovalDate}
                        slotProps={{
                            textField: {
                                variant: 'standard',
                                clearable: true,
                                error: toShowError,
                                helperText: toShowError ? (currentError ?? '') : undefined,
                                InputProps: {
                                    classes: getFieldStyle(
                                        isRequired,
                                        detail,
                                        field,
                                        classes,
                                        notSavedClasses,
                                        treatmentValidationUnsavedValues,
                                        actualsTreatmentValidationUnsavedValues,
                                        activityValidationUnsavedValues,
                                        actualsActivityValidationUnsavedValues
                                    ),
                                    readOnly: isReadOnly,
                                },
                            },
                        }}
                        maxDate={isPast ? new Date() : null}
                        onError={(reason, value) => {
                            switch (reason) {
                                case 'maxDate':
                                    setToShowError(true);
                                    setCurrentError(
                                        `Date should not be after ${formatDateForDisplay(new Date())}`
                                    );
                                    break;
                                case null:
                                    setToShowError(false);
                                    setCurrentError('');
                                    break;
                                default:
                                    break;
                            }
                        }}
                    />
                </LocalizationProvider>
            </ThemeProvider>
        </FormControl>
    );
}
